import CloseIcon from '@mui/icons-material/CloseRounded';
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';

import { CompanySealed } from './CompanySealed';
import { FlowComplete } from './FlowComplete';
import { FlowProgress } from './FlowProgress';
import { chapterStateOf, chaptersOf } from './grouping';
import { titleOf } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import { PayStep } from './PayStep';
import { SignStep } from './SignStep';
import { useGuidedFlow } from './useGuidedFlow';

interface GuidedFlowDialogProps {
  items: OutstandingItem[];
  /** Opens the queue at this item — set when the flow is entered from one
   *  card's own button rather than from the hero. */
  startAt?: string;
  onClose: () => void;
}

/**
 * The guided flow: everything outstanding, one decision per screen.
 *
 * This is the thing a table could never be. A director arriving from an SMS
 * taps once and is walked through signing and paying every outstanding item in
 * urgency order, never seeing two decisions at the same time and never being
 * asked to find the next one themselves.
 *
 * Full-screen below `sm` — already themed — because a centred modal with
 * margins wastes a third of a phone screen on the one flow that most needs the
 * room (§4).
 */
export const GuidedFlowDialog = ({ items, startAt, onClose }: GuidedFlowDialogProps) => {
  const flow = useGuidedFlow(items, startAt);
  const { current } = flow;

  // The company the director just finished, shown once above the next one's
  // first step. Derived rather than stored: the chapter immediately before the
  // current position either sealed or it did not, and asking that question of
  // the frozen queue each render is what keeps the banner honest when the
  // director steps back.
  const chapters = chaptersOf(flow.steps);
  const activeIndex = chapters.findIndex(
    (chapter) =>
      flow.index >= chapter.startIndex &&
      flow.index < chapter.startIndex + chapter.steps.length,
  );
  const previous = activeIndex > 0 ? chapters[activeIndex - 1] : undefined;
  const justSealed =
    previous !== undefined &&
    chapterStateOf(previous, flow.index, flow.outcomes) === 'sealed' &&
    // Only at the boundary. Once the director is a step or more into the next
    // company, the seal belongs to the rail's history, not to this banner.
    flow.index === previous.startIndex + previous.steps.length
      ? previous
      : undefined;

  // Opening a flow with nothing to do would render an empty dialog. The list
  // guards this, but the queue is the authority on its own emptiness.
  if (flow.steps.length === 0) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent>
          <FlowComplete doneCount={0} skippedCount={0} onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="flex items-start gap-2 pb-2">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
          {current === undefined ? (
            <Typography variant="h2" component="span">
              All done
            </Typography>
          ) : (
            /* The task alone. It is what the director is being asked to
               decide, so it takes the header and is never truncated — clipping
               it is what made two ASIC forms indistinguishable. The company is
               carried by the progress rail below, which groups the queue by
               company; repeating it here spent a line of a phone header on
               something already on screen. */
            <Typography variant="h2" component="span" className="text-balance">
              {current.kind === 'sign' ? 'Sign' : 'Pay'} {titleOf(current.item)}
            </Typography>
          )}
        </div>

        <IconButton onClick={onClose} aria-label="Close" edge="end" className="shrink-0">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="flex flex-col gap-5">
        {current !== undefined && (
          <FlowProgress steps={flow.steps} index={flow.index} outcomes={flow.outcomes} />
        )}

        {justSealed !== undefined && <CompanySealed companyName={justSealed.companyName} />}

        {current === undefined ? (
          <FlowComplete
            doneCount={flow.doneCount}
            skippedCount={flow.skippedCount}
            onClose={onClose}
          />
        ) : current.kind === 'sign' ? (
          <SignStep
            key={current.id}
            item={current.item}
            onDone={flow.complete}
            onSkip={flow.skip}
          />
        ) : (
          <PayStep
            key={current.id}
            item={current.item}
            onDone={flow.complete}
            onSkip={flow.skip}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
